import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { whatsappCloudService, WhatsAppCloudTemplate } from '@/services/integrations/whatsappCloudService';

interface Props {
  template: WhatsAppCloudTemplate;
  /** Called after a save so the parent can reload the effective values. */
  onSaved: () => void;
}

/**
 * Sample value per variable — what agents see as a hint when they send this
 * template from the inbox. Starts from Meta's approval examples; edits are CRM-only,
 * so saving never sends the template back to Meta for review. A blank field falls
 * back to Meta's example.
 */
const SampleValuesPanel: React.FC<Props> = ({ template, onSaved }) => {
  const params = useMemo(
    () => [...(template.parameters || [])].sort((a, b) => a.index - b.index),
    [template.parameters],
  );
  const urlButtons = useMemo(
    () => (template.buttons || [])
      .map((b, index) => ({ ...b, index }))
      .filter((b) => b.type === 'URL' && /\{\{\s*1\s*\}\}/.test(b.url || '')),
    [template.buttons],
  );

  const initialBody = useMemo(
    () => Object.fromEntries(params.map((p) => [p.index, p.example || ''])) as Record<number, string>,
    [params],
  );
  const initialButtons = useMemo(
    () => Object.fromEntries(
      urlButtons.map((b) => [b.index, template.button_examples?.[String(b.index)] || '']),
    ) as Record<number, string>,
    [urlButtons, template.button_examples],
  );

  const [body, setBody] = useState(initialBody);
  const [buttons, setButtons] = useState(initialButtons);
  const [saving, setSaving] = useState(false);

  // After a save the parent reloads; a cleared field comes back as Meta's example.
  useEffect(() => { setBody(initialBody); }, [initialBody]);
  useEffect(() => { setButtons(initialButtons); }, [initialButtons]);

  const dirty = JSON.stringify(body) !== JSON.stringify(initialBody)
    || JSON.stringify(buttons) !== JSON.stringify(initialButtons);

  if (params.length === 0 && urlButtons.length === 0) {
    return (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-2">Variables</div>
        <p className="text-xs text-gray-400">No dynamic parameters — static template.</p>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await whatsappCloudService.setSampleValues(template.id, { body, buttons });
      toast.success('Sample values saved');
      onSaved();
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.message : undefined;
      toast.error(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-gray-500">Variables · sample values</div>
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        )}
      </div>
      <div className="space-y-2">
        {params.map((p) => (
          <div key={p.index} className="flex items-center gap-2">
            <span className="w-12 flex-shrink-0 px-1.5 py-1 rounded bg-brandColor/10 text-brandColor text-xs font-mono text-center">
              {`{{${p.index}}}`}
            </span>
            <input
              value={body[p.index] || ''}
              onChange={(e) => setBody((v) => ({ ...v, [p.index]: e.target.value }))}
              placeholder="Sample value"
              className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
            />
          </div>
        ))}
        {urlButtons.map((b) => (
          <div key={`btn-${b.index}`}>
            <div className="text-[11px] text-gray-500 mb-1">
              “{b.text}” button — <span className="font-mono">{b.url}</span>
            </div>
            <input
              value={buttons[b.index] || ''}
              onChange={(e) => setButtons((v) => ({ ...v, [b.index]: e.target.value }))}
              placeholder="Sample {{1}} value"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
            />
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-gray-400">
        Shown to agents as hints when sending from the inbox. Saved in the CRM only — the
        template is not re-submitted to Meta. Leave a field blank to use Meta&apos;s example.
      </p>
    </div>
  );
};

export default SampleValuesPanel;
